/**
 * Phone Number Formatting Utility
 * 
 * Provides locale-aware phone number formatting using libphonenumber-js
 */

import { LanguageContext } from '@axon-enterprise/i18n-helpers';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';
import { useContext, useEffect, useState } from 'react';

import { logger } from './logger';

/**
 * Base support phone number in E.164 format
 */
const SUPPORT_PHONE_BASE = '+18442269234';

/**
 * Maps locale to country code for phone number formatting
 * @param locale - Language locale (e.g., 'en-us', 'fr-ca')
 * @returns ISO country code (e.g., 'US', 'CA')
 */
const getCountryCodeFromLocale = (locale: string): CountryCode => {
  const localeMap: Record<string, CountryCode> = {
    'en-us': 'US',
    'fr-ca': 'CA',
    'es': 'US', // Default Spanish to US
    'sk': 'US', // Default Slovak to US
  };
  
  return localeMap[locale.toLowerCase()] || 'US';
};

/**
 * Hook to get formatted support phone number based on current locale
 * Formats phone numbers using the country code derived from the user's locale
 * 
 * @returns Formatted phone number string
 */
export const useSupportPhoneDisplay = (): string => {
  const { language } = useContext(LanguageContext);
  const [countryCode, setCountryCode] = useState<CountryCode>(() => 
    getCountryCodeFromLocale(language)
  );

  // Update country code when locale changes
  useEffect(() => {
    setCountryCode(getCountryCodeFromLocale(language));
  }, [language]);

  try {
    const phoneNumber = parsePhoneNumberFromString(SUPPORT_PHONE_BASE, countryCode);
    if (!phoneNumber) {
      logger.error('Failed to parse support phone number');
      return SUPPORT_PHONE_BASE;
    }

    // Use national format if number belongs to same region, otherwise international
    return phoneNumber.country === countryCode
      ? phoneNumber.formatNational()
      : phoneNumber.formatInternational();
  } catch (error) {
    logger.error('Error formatting support phone number', error);
    return SUPPORT_PHONE_BASE;
  }
};

/**
 * Gets the tel: URI for the support phone number
 * @returns tel: URI string
 */
export const getSupportPhoneTel = (): string => {
  return `tel:${SUPPORT_PHONE_BASE}`;
};

/**
 * Formats a phone number based on locale (synchronous, non-hook version)
 * For use in contexts where hooks cannot be used (e.g., flow engine initialization)
 * 
 * @param phoneNumber - Phone number in E.164 format (e.g., '+18442269234')
 * @param locale - Language locale (e.g., 'en-us', 'fr-ca')
 * @returns Formatted phone number string
 */
export const formatPhoneForLocale = (phoneNumber: string, locale: string): string => {
  const countryCode = getCountryCodeFromLocale(locale);
  
  try {
    const parsed = parsePhoneNumberFromString(phoneNumber, countryCode);
    if (!parsed) {
      logger.error('Failed to parse phone number', { phoneNumber, locale });
      return phoneNumber;
    }

    // Use national format if number belongs to same region, otherwise international
    return parsed.country === countryCode
      ? parsed.formatNational()
      : parsed.formatInternational();
  } catch (error) {
    logger.error('Error formatting phone number', error, { phoneNumber, locale });
    return phoneNumber;
  }
};

/**
 * Gets the formatted support phone number for a specific locale (synchronous)
 * For use in contexts where hooks cannot be used (e.g., flow engine initialization)
 * 
 * @param locale - Language locale (e.g., 'en-us', 'fr-ca')
 * @returns Formatted support phone number string
 */
export const getSupportPhoneForLocale = (locale: string): string => {
  return formatPhoneForLocale(SUPPORT_PHONE_BASE, locale);
};

